import React from 'react';
import { StyleSheet, View, type ViewProps } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { AnimatedViews, Container, theme, useResponsive } from '@sg/garnish';

import { HomeGenericCategory } from '../HomeCategory';
import { HomeContainer } from '../HomeContainer';
import { HOME_PRODUCT_CARD_SIZES_REGULAR, HOME_PRODUCT_CARD_SIZES_LARGE } from '../HomeProductCard';

import type { HomeProductCardSize } from '../HomeProductCard/HomeProductCard.types';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeLoading = (props: Props) => {
  const { size = 'regular', bannerSize, bannerStyle } = props;

  const { match } = useResponsive();

  const placeholderIndexes = [...Array(PLACEHOLDER_PRODUCTS).keys()];

  // ─── Styles ────────────────────────────────────────────────────────────────

  const bannerXSStyle = bannerSize === 'small' ? styles.bannerSmallXS : styles.bannerLargeXS;
  const bannerSizeStyle = match([bannerXSStyle, styles.bannerSM, styles.bannerMD]);
  const bannerStyles = [styles.banner, bannerSizeStyle, bannerStyle];
  const categoryStyle = match([styles.categoryXS, styles.categorySM]);
  const categoryTitleStyle = match([styles.categoryTitleXS, styles.categoryTitleSM]);

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <HomeContainer scrollEnabled={false}>
      <HomeContainer.Content>
        <AnimatedViews.Pulse style={bannerStyles} />

        <Container removeSpacing style={categoryStyle}>
          <AnimatedViews.Pulse index={1} style={categoryTitleStyle} />

          <HomeGenericCategory size={size}>
            {placeholderIndexes.map((index) => (
              <ProductCardPlaceholder key={index} size={size} index={index + 2} />
            ))}
          </HomeGenericCategory>
        </Container>
      </HomeContainer.Content>
    </HomeContainer>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const ProductCardPlaceholder = (props: ProductCardPlaceholderProps) => {
  const { size = 'regular', index } = props;

  const { match } = useResponsive();

  const cardSizes = size === 'regular' ? HOME_PRODUCT_CARD_SIZES_REGULAR : HOME_PRODUCT_CARD_SIZES_LARGE; // prettier-ignore
  const imageSize = match(cardSizes);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const sizeStyle = useStyle(() => ({ width: imageSize }), [imageSize]);
  const imageSizeStyle = useStyle(() => ({ height: imageSize }), [imageSize]);
  const containerStyles = [styles.productCard, sizeStyle];
  const imageStyles = [styles.productImage, imageSizeStyle];
  const titleStyle = match([styles.productTitleXS, styles.productTitleSM]);
  const descriptionLineStyle = match([styles.productDescriptionXS, styles.productDescriptionSM]);
  const descriptionShortLineStyles = [descriptionLineStyle, styles.productDescriptionShort];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedViews.Pulse index={index} style={containerStyles}>
      <View style={imageStyles} />
      <View style={titleStyle} />

      <View>
        <View style={descriptionLineStyle} />
        <View style={descriptionShortLineStyles} />
      </View>

      <View style={styles.productCost} />
    </AnimatedViews.Pulse>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const PLACEHOLDER_PRODUCTS = 3;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  banner: {
    width: '100%',
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  bannerSmallXS: {
    minHeight: 160,
  },
  bannerLargeXS: {
    minHeight: 380,
  },
  bannerSM: {
    aspectRatio: 1.8,
  },
  bannerMD: {
    minHeight: 800,
    maxHeight: 1080,
  },
  categoryXS: {
    gap: theme.spacing['4'],
  },
  categorySM: {
    alignItems: 'center',
    gap: theme.spacing['8'],
    paddingTop: theme.spacing['8'],
  },
  categoryTitleXS: {
    width: 180,
    height: 14,
    marginVertical: 5,
    marginHorizontal: theme.spacing['6'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  categoryTitleSM: {
    width: 360,
    height: 32,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productCard: {
    gap: theme.spacing['2'],
  },
  productImage: {
    borderRadius: theme.radius.medium,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productTitleXS: {
    width: '72%',
    height: 12,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productTitleSM: {
    width: '64%',
    height: 14,
    marginTop: 13,
    marginBottom: 5,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productDescriptionXS: {
    width: '100%',
    height: 10,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productDescriptionSM: {
    width: '100%',
    height: 12,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  productDescriptionShort: {
    width: '64%',
  },
  productCost: {
    width: 44,
    height: 10,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  size?: HomeProductCardSize;
  bannerSize: 'small' | 'large';
  bannerStyle?: ViewProps['style'];
};

type ProductCardPlaceholderProps = {
  size?: HomeProductCardSize;
  index: number;
};
